import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxIcon from '@mui/icons-material/Inbox';
import { IPD_COLORS, ipdCardSx } from './ipdColors';
import { tableContainerSx } from './ipdTable';

/** Grid height for list pages — leaves room for header/tabs */
export const LIST_GRID_HEIGHT = 'calc(100dvh - 260px)';

/** Grid height for dashboard pages with stat cards above */
export const DASHBOARD_GRID_HEIGHT = 'calc(100dvh - 280px)';

export default function IpdDataGrid({
  rows, columns, loading, error, onRetry,
  emptyMessage = 'No records found', emptyDescription, emptyAction,
  pageSize = 25, pageSizeOptions = [10, 25, 50],
  getRowId,
  fillHeight = false,
  embedded = false,
  gridHeight = LIST_GRID_HEIGHT,
  ...rest
}) {
  const useFixedHeight = fillHeight || rest.autoHeight === false;
  const shellSx = useFixedHeight
    ? { height: gridHeight, minHeight: 360, display: 'flex', flexDirection: 'column', justifyContent: 'center' }
    : {};
  const cardSx = { ...ipdCardSx, color: IPD_COLORS.textPrimary };
  const surfaceSx = embedded
    ? { border: 'none', boxShadow: 'none', backgroundColor: 'transparent' }
    : cardSx;

  if (error) {
    return (
      <Box sx={{ ...surfaceSx, ...shellSx, p: 4, textAlign: 'center' }}>
        <Typography sx={{ color: IPD_COLORS.error.text, mb: 2 }}>{error}</Typography>
        {onRetry && (
          <Button onClick={onRetry} variant="outlined" startIcon={<RefreshIcon />}
            sx={{ color: IPD_COLORS.primary, borderColor: IPD_COLORS.primary }}>
            Retry
          </Button>
        )}
      </Box>
    );
  }

  if (!loading && rows && rows.length === 0) {
    return (
      <Box sx={{ ...surfaceSx, ...shellSx, p: 6, textAlign: 'center' }}>
        <InboxIcon sx={{ color: IPD_COLORS.textSecondary, fontSize: 48, mb: 1.5, opacity: 0.7 }} />
        <Typography sx={{ color: IPD_COLORS.textPrimary, fontWeight: 600, fontSize: 15, mb: 0.75 }}>
          {emptyMessage}
        </Typography>
        {emptyDescription && (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 14, mb: 2.5, maxWidth: 360, mx: 'auto' }}>
            {emptyDescription}
          </Typography>
        )}
        {!emptyDescription && <Box sx={{ mb: 2.5 }} />}
        {emptyAction}
      </Box>
    );
  }

  return (
    <Box sx={{
      ...tableContainerSx,
      ...surfaceSx,
      ...(useFixedHeight ? { height: gridHeight, minHeight: 360, display: 'flex', flexDirection: 'column' } : {}),
    }}>
      <DataGrid
        rows={rows || []}
        columns={columns}
        loading={loading}
        getRowId={getRowId || (row => {
          const id = row.id || row._id || row.item_id || row.vendor_id || row.store_id
            || row.transaction_id || row.grn_id || row.purchase_order_id || row.rfq_id
            || row.ledger_id || row.uom_id || row.category_id
            || row.material_group_id || row.item_class_id;
          if (!id) {
            try { return JSON.stringify(row); } catch { return String(Math.random()); }
          }
          return id;
        })}
        initialState={{ pagination: { paginationModel: { pageSize } } }}
        pageSizeOptions={pageSizeOptions}
        disableRowSelectionOnClick
        autoHeight={!useFixedHeight}
        sx={{
          border: 'none',
          flex: 1,
          minHeight: 0,
          ...(useFixedHeight ? { height: '100%' } : {}),
        }}
        slotProps={{
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        }}
        {...rest}
      />
    </Box>
  );
}
